import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FiShield, FiFilter, FiDownload, FiChevronDown, FiChevronUp, FiUser, FiClock } from 'react-icons/fi';
import { auditAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { formatDateTimeFull } from '../../utils/dateFormat';
import { auditEventLabel, auditEntityLabel, auditDescription } from '../../utils/auditI18n';
import PageHeader from '../../components/PageHeader/PageHeader';
import './AuditLog.scss';

function AuditLog() {
  const { t, i18n } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [eventType, setEventType] = useState('');
  const [entityType, setEntityType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const EVENT_TYPES = [
    { value: '', label: t('audit.eventTypes.all') },
    { value: 'login', label: t('audit.eventTypes.login') },
    { value: 'logout', label: t('audit.eventTypes.logout') },
    { value: 'user_invited', label: t('audit.eventTypes.user_invited') },
    { value: 'role_changed', label: t('audit.eventTypes.role_changed') },
    { value: 'contract_created', label: t('audit.eventTypes.contract_created') },
    { value: 'contract_edited', label: t('audit.eventTypes.contract_edited') },
    { value: 'contract_deleted', label: t('audit.eventTypes.contract_deleted') },
    { value: 'workflow_approved', label: t('audit.eventTypes.workflow_approved') },
    { value: 'workflow_rejected', label: t('audit.eventTypes.workflow_rejected') },
    { value: 'tasks_reassigned', label: t('audit.eventTypes.tasks_reassigned') },
    { value: 'workflow_assignee_replaced', label: t('audit.eventTypes.workflow_assignee_replaced') },
    { value: 'document_uploaded', label: t('audit.eventTypes.document_uploaded') },
    { value: 'document_downloaded', label: t('audit.eventTypes.document_downloaded') },
    { value: 'template_created', label: t('audit.eventTypes.template_created') },
    { value: 'template_updated', label: t('audit.eventTypes.template_updated') },
    { value: 'signature_requested', label: t('audit.eventTypes.signature_requested') },
    { value: 'signature_completed', label: t('audit.eventTypes.signature_completed') },
    { value: 'settings_modified', label: t('audit.eventTypes.settings_modified') },
  ];

  const ENTITY_TYPES = [
    { value: '', label: t('audit.entityTypes.all') },
    { value: 'contract', label: t('audit.entityTypes.contract') },
    { value: 'user', label: t('audit.entityTypes.user') },
    { value: 'template', label: t('audit.entityTypes.template') },
    { value: 'workflow', label: t('audit.entityTypes.workflow') },
    { value: 'settings', label: t('audit.entityTypes.settings') },
  ];

  const loadEvents = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (eventType) params.eventType = eventType;
      if (entityType) params.entityType = entityType;
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;
      const { data } = await auditAPI.list(params);
      setEvents(data.events || []);
      setPagination(data.pagination || { page: 1, limit: 50, total: 0, pages: 0 });
    } catch (err) {
      toast.error(t('audit.messages.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [eventType, entityType, dateFrom, dateTo, t]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const toggleExpand = (eventId) => {
    setExpandedEvent(expandedEvent === eventId ? null : eventId);
  };

  const getEventIcon = (type) => {
    if (type?.includes('login') || type?.includes('logout')) return '🔐';
    if (type?.includes('contract_created')) return '📄';
    if (type?.includes('contract_edited')) return '✏️';
    if (type?.includes('contract_deleted')) return '🗑️';
    if (type?.includes('reassign')) return '🔁';
    if (type?.includes('workflow')) return '✅';
    if (type?.includes('document')) return '📎';
    if (type?.includes('template')) return '📋';
    if (type?.includes('user') || type?.includes('role')) return '👤';
    if (type?.includes('signature')) return '✍️';
    if (type?.includes('settings')) return '⚙️';
    return '📌';
  };

  const getEventSeverityClass = (type) => {
    if (type?.includes('deleted') || type?.includes('rejected')) return 'audit-page__event--danger';
    if (type?.includes('created') || type?.includes('approved') || type?.includes('completed')) return 'audit-page__event--success';
    if (type?.includes('edited') || type?.includes('updated') || type?.includes('changed')) return 'audit-page__event--warning';
    return 'audit-page__event--info';
  };

  // Formats the actor for an event. When the action happened inside an
  // impersonation session, shows "on behalf of" (e.g. "X yerine Y").
  const formatActor = (e) => {
    const name = (u) => (u?.firstName ? `${u.firstName} ${u.lastName || ''}`.trim() : null);
    const actor = name(e.actorId) || t('common.system');
    // A delegated action is recorded against the delegator, with the delegate
    // named as the one who carried it out — both sides stay visible here.
    const delegate = name(e.delegateId);
    if (delegate) return t('common.byDelegation', { delegate, delegator: actor });
    const impersonator = name(e.impersonatorId);
    return impersonator
      ? t('common.onBehalfOf', { impersonator, impersonated: actor })
      : actor;
  };

  const handleExportCSV = () => {
    const headers = [
      t('audit.export.timestamp'),
      t('audit.export.eventType'),
      t('audit.export.actor'),
      t('audit.export.entityType'),
      t('audit.export.entityId'),
      t('audit.export.description'),
      t('audit.export.ipAddress'),
    ];
    const rows = events.map((e) => [
      e.createdAt ? format(new Date(e.createdAt), 'yyyy-MM-dd HH:mm:ss') : '',
      e.eventType || '',
      formatActor(e),
      e.entityType || '',
      e.entityId || '',
      e.description || '',
      e.ipAddress || '',
    ]);
    const csv = [headers.join(','), ...rows.map((r) => r.map((c) => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(t('audit.messages.exportSuccess'));
  };

  return (
    <div className="audit-page">
      <PageHeader
        label={t('nav.accessCompliance', 'Access & Compliance')}
        labelIcon={<FiShield />}
        title={t('audit.title')}
        description={t('audit.subtitle', { count: pagination.total })}
        actions={(
          <>
            <button className="btn btn-outline" onClick={() => setShowFilters(!showFilters)}>
              <FiFilter /> {t('common.filters')}
            </button>
            <button className="btn btn-outline" onClick={handleExportCSV}>
              <FiDownload /> {t('audit.exportCSV')}
            </button>
          </>
        )}
      />

      {/* Filters */}
      {showFilters && (
        <div className="audit-page__filters card">
          <div className="audit-page__filter-row">
            <div className="form-group">
              <label>{t('audit.filters.eventType')}</label>
              <select value={eventType} onChange={(e) => setEventType(e.target.value)}>
                {EVENT_TYPES.map((et) => (
                  <option key={et.value} value={et.value}>{et.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>{t('audit.filters.entityType')}</label>
              <select value={entityType} onChange={(e) => setEntityType(e.target.value)}>
                {ENTITY_TYPES.map((et) => (
                  <option key={et.value} value={et.value}>{et.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>{t('audit.filters.fromDate')}</label>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            <div className="form-group">
              <label>{t('audit.filters.toDate')}</label>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
          </div>
          <div className="audit-page__filter-actions">
            <button className="btn btn-sm btn-outline" onClick={() => {
              setEventType(''); setEntityType(''); setDateFrom(''); setDateTo('');
            }}>{t('common.clearFilters')}</button>
          </div>
        </div>
      )}

      {/* Events Timeline */}
      <div className="card">
        {loading ? (
          <div className="loading-spinner">{t('audit.loadingEvents')}</div>
        ) : events.length === 0 ? (
          <div className="empty-state">
            <FiShield style={{ fontSize: '2.5rem', color: '#6b7280', marginBottom: '1rem' }} />
            <h3>{t('audit.noEventsFound')}</h3>
            <p>{t('audit.noEventsDesc')}</p>
          </div>
        ) : (
          <div className="audit-page__timeline">
            {events.map((event) => (
              <div key={event._id} className="audit-page__event-row">
                <div
                  className="audit-page__event-main"
                  onClick={() => toggleExpand(event._id)}
                >
                  <div className="audit-page__event-left">
                    <span className="audit-page__event-icon">{getEventIcon(event.eventType)}</span>
                    <div className="audit-page__event-info">
                      <div className="audit-page__event-header">
                        <span className={`audit-page__event-type ${getEventSeverityClass(event.eventType)}`}>
                          {auditEventLabel(event.eventType, t)}
                        </span>
                        {event.entityType && (
                          <span className="audit-page__event-entity">
                            {auditEntityLabel(event.entityType, t)}
                          </span>
                        )}
                      </div>
                      <p className="audit-page__event-description">
                        {auditDescription(event.description, t, i18n.language)}
                      </p>
                      <div className="audit-page__event-meta">
                        <span className="audit-page__event-actor">
                          <FiUser />
                          {formatActor(event)}
                        </span>
                        <span className="audit-page__event-time">
                          <FiClock />
                          {formatDateTimeFull(event.createdAt)}
                        </span>
                        {event.ipAddress && (
                          <span className="audit-page__event-ip">IP: {event.ipAddress}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button className="audit-page__event-expand">
                    {expandedEvent === event._id ? <FiChevronUp /> : <FiChevronDown />}
                  </button>
                </div>

                {expandedEvent === event._id && (
                  <div className="audit-page__event-detail">
                    <div className="audit-page__event-detail-grid">
                      <div>
                        <label>{t('audit.detail.eventId')}</label>
                        <span>{event._id}</span>
                      </div>
                      <div>
                        <label>{t('audit.detail.entityId')}</label>
                        <span>{event.entityId || '—'}</span>
                      </div>
                      <div>
                        <label>{t('audit.detail.userAgent')}</label>
                        <span className="audit-page__event-ua">{event.userAgent || '—'}</span>
                      </div>
                      {event.before && (
                        <div className="audit-page__event-changes">
                          <label>{t('audit.detail.before')}</label>
                          <pre>{JSON.stringify(event.before, null, 2)}</pre>
                        </div>
                      )}
                      {event.after && (
                        <div className="audit-page__event-changes">
                          <label>{t('audit.detail.after')}</label>
                          <pre>{JSON.stringify(event.after, null, 2)}</pre>
                        </div>
                      )}
                      {event.metadata && Object.keys(event.metadata).length > 0 && (
                        <div className="audit-page__event-changes">
                          <label>{t('audit.detail.metadata')}</label>
                          <pre>{JSON.stringify(event.metadata, null, 2)}</pre>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="audit-page__pagination">
            <span className="audit-page__pagination-info">
              {t('audit.pagination.showing', {
                from: (pagination.page - 1) * pagination.limit + 1,
                to: Math.min(pagination.page * pagination.limit, pagination.total),
                total: pagination.total,
              })}
            </span>
            <div className="audit-page__pagination-buttons">
              <button
                className="btn btn-sm btn-outline"
                disabled={pagination.page <= 1}
                onClick={() => loadEvents(pagination.page - 1)}
              >
                {t('common.previous')}
              </button>
              <button
                className="btn btn-sm btn-outline"
                disabled={pagination.page >= pagination.pages}
                onClick={() => loadEvents(pagination.page + 1)}
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AuditLog;
